import { useState, useEffect, useCallback } from "react";

const audioContext = new (window.AudioContext || window.webkitAudioContext)();

const base = process.env.PUBLIC_URL || "";
const soundFiles = {
  punch: `${base}/assets/audio/punch.wav`,
  left: `${base}/assets/audio/left.wav`,
  right: `${base}/assets/audio/right.wav`,
  pop: `${base}/assets/audio/pop.wav`,
  bell: `${base}/assets/audio/bell.wav`,
  beep: `${base}/assets/audio/beep.wav`,
  w1: `${base}/assets/audio/workout-1.wav`,
  dodge: `${base}/assets/audio/dodge.wav`,
  rest: `${base}/assets/audio/rest.wav`,
  beaten: `${base}/assets/audio/beaten.wav`,
};

const useAudio = () => {
  const [sounds, setSounds] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  const loadSound = useCallback(async (name, url) => {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      setSounds((prevSounds) => ({ ...prevSounds, [name]: audioBuffer }));
    } catch (error) {
      console.error(`Error loading sound: ${name}`, error);
    }
  }, []);

  const playSound = useCallback(
    (name, options = {}) => {
      if (audioContext.state === "suspended") {
        audioContext.resume();
      }
      const buffer = sounds[name];
      if (!buffer) {
        console.warn(`Sound '${name}' not loaded or not found.`);
        return;
      }

      const source = audioContext.createBufferSource();
      const gainNode = audioContext.createGain();

      source.buffer = buffer;

      // Add sound variations for more natural feel
      const {
        pitchVariation = true,
        volumeVariation = true,
        basePitch = 1.0,
        baseVolume = 1.0,
      } = options;

      // Apply pitch variation (±40%)
      if (pitchVariation) {
        const pitchRange = 0.4;
        const randomPitch = (Math.random() - 0.5) * pitchRange;
        source.playbackRate.value = basePitch + randomPitch;
      } else {
        source.playbackRate.value = basePitch;
      }

      // Apply volume variation (±30%)
      if (volumeVariation) {
        const volumeRange = 0.3;
        const randomVolume = (Math.random() - 0.5) * volumeRange;
        gainNode.gain.value = Math.max(
          0.1,
          Math.min(1.0, baseVolume + randomVolume)
        );
      } else {
        gainNode.gain.value = baseVolume;
      }

      // Connect audio graph: source -> gain -> destination
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);
      source.start(0);
    },
    [sounds]
  );

  // Convenience method specifically for punch sounds with natural variations
  const playPunchSound = useCallback(
    (side = null) => {
      playSound("punch", {
        pitchVariation: true,
        volumeVariation: true,
        basePitch: 1.0,
        baseVolume: 0.3, // Slightly lower volume than other sounds
      });
    },
    [playSound]
  );

  // Convenience method for bell sounds (round start/end)
  const playBellSound = useCallback(() => {
    playSound("bell", {
      pitchVariation: false, // Keep bell pitch consistent
      volumeVariation: false, // Keep bell volume consistent
      basePitch: 1.0,
      baseVolume: 0.7,
    });
  }, [playSound]);

  // Convenience method for beep sounds (countdown)
  const playBeepSound = useCallback(() => {
    playSound("beep", {
      pitchVariation: false, // Keep beep pitch consistent for clarity
      volumeVariation: false, // Keep beep volume consistent
      basePitch: 1.0,
      baseVolume: 0.4,
    });
  }, [playSound]);

  // Convenience method for pop sounds (combo generation)
  const playPopSound = useCallback(() => {
    playSound("pop", {
      pitchVariation: true,
      volumeVariation: true,
      basePitch: 1.0,
      baseVolume: 1.0,
    });
  }, [playSound]);

  // Convenience method for dodge sounds
  const playDodgeSound = useCallback(() => {
    playSound("dodge", {
      pitchVariation: true,
      volumeVariation: true,
      basePitch: 1.0,
      baseVolume: 1.0,
    });
  }, [playSound]);

  // Convenience method for rest sounds
  const playRestSound = useCallback(() => {
    playSound("rest", {
      pitchVariation: false,
      volumeVariation: false,
      basePitch: 1.0,
      baseVolume: 1.0,
    });
  }, [playSound]);

  const playBeaten = useCallback(
    () =>
      playSound("beaten", {
        pitchVariation: false,
        volumeVariation: false,
        basePitch: 1.0,
        baseVolume: 0.4,
      }),
    [playSound]
  );

  useEffect(() => {
    const loadAllSounds = async () => {
      const promises = Object.entries(soundFiles).map(([name, url]) =>
        loadSound(name, url)
      );
      await Promise.all(promises);
      setIsLoaded(true);
    };

    if (!isLoaded) {
      loadAllSounds();
    }
  }, [loadSound, isLoaded]);

  // Resume AudioContext on first user interaction
  useEffect(() => {
    const resumeAudio = () => {
      if (audioContext.state === "suspended") {
        audioContext.resume();
      }
      document.body.removeEventListener("click", resumeAudio);
    };
    document.body.addEventListener("click", resumeAudio);

    return () => {
      document.body.removeEventListener("click", resumeAudio);
    };
  }, []);

  return {
    playSound,
    playPunchSound,
    playBellSound,
    playBeepSound,
    playPopSound,
    playDodgeSound,
    playRestSound,
    playBeaten,
    isLoaded,
  };
};

export default useAudio;
